import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Cart, CartDisabled, Favorite } from '@yme/assets/icons';
import { Button, CONTAINED_VARIANT, GHOST_VARIANT, ICON_ONLY, NO_ICON, SIZE_48, TEXT_VARIANT, TRAILING_ICON, } from '@yme/atoms/button';
import { Dropdown } from '@yme/atoms/dropdown';
import { addProductToCartFailure } from '@yme/common/redux/cart/actions';
import { CartErrorMessage } from '@yme/common/redux/cart/constants';
import { getIsCartConvertedToOrder } from '@yme/common/redux/cart/selectors';
import { addProductToCart, addProductToExternalCart } from '@yme/common/redux/cart/services';
import { getDropdownItems } from '@yme/components/ShoppingCart/CartItem/helpers';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { useWishlist } from '@yme/hooks/useWishlist';
import { setIsModalShowed } from '@yme/modals/actions';
import { MODALS } from '@yme/modals/constants';
import { ButtonLinkStyled } from '@yme/molecules/buttons';
import { setFasletAnalyticTrigger, setItemAddedToCart, } from '@yme/pdp-components/Faslet/redux/actions';
import { getIsItemAddedToCart } from '@yme/pdp-components/Faslet/selectors';
import { setProductQuantity, getLinkForTestRide, getProductCardLabels, getProductQuantity, getSelectedCompatibilityProduct, } from '@yme/pdp-components/ProductCard';
import { INCOMPATIBLE, NEED_CHECK, NEED_REQUIRED_PARTS, } from '@yme/pdp-components/ProductCard/CardAccessories/constants';
import { getEcommerceEnabled, getIsExternalCartEnabled, getRTL } from '@yme/selectors/common';
import { getProductDetails } from '@yme/selectors/products';
import { useTranslation } from '@yme/services/i18n';
import { DigitalDataMutation, setDigitalData } from '@yme/utils/common';
import { MAX_STOCK_VALUE } from '@yme/utils/constants';
import { BUTTONS_PANEL_QUANTITY_WIDTH } from './constants';
import { getButtonPanelParam, getModalDescription } from './helpers';
import { ButtonSelectorContainerStyled, ButtonStyled, ButtonsGroupInnerStyled, ButtonsGroupWrapperStyled, QuantitySelectorWrapperStyled, } from './styled';
export function ButtonsPanel({ addToCartButtonLabel, outOfStockButtonLabel, requestTestRideButtonLabel, productStock, eCommerceEnabledVariant, priceAttribute, variantSku, isMaxStockValue, hyperdriveEndpoints: { createCart, updateCart }, isVarianAvailable = false, filterOptionByProductType, blockWrapperWidth, requiredProductSku, selectModelHandler, accessoriesCartState, isAccessoryOrApparel, preparedProductsUrls = [], }) {
    const [isNotEnoughStockLeft, setIsNotEnoughStockLeft] = useState(false);
    const [isButtonsColumn, setIsButtonsColumn] = useState(false);
    const { isLiked: checkIsLiked, onLike, isWishlistEnabled } = useWishlist();
    const { t } = useTranslation();
    const isLiked = checkIsLiked(variantSku);
    const wishlistClass = 'favorite-button' + (isLiked ? ' favorite-button--active' : '');
    const wishlistTooltipTitle = isLiked
        ? t('wishlist_buttonTooltipToRemoveItem_label')
        : t('wishlist_buttonTooltipToAddItem_label');
    const ecommerceEnabled = useSelector(getEcommerceEnabled);
    const isRightToLeft = useSelector(getRTL);
    const isCartConvertedToOrder = useSelector(getIsCartConvertedToOrder);
    const labels = useSelector(getProductCardLabels);
    const { data: productDetailsData } = useSelector(getProductDetails);
    const selectedProduct = useSelector(getSelectedCompatibilityProduct);
    const productQuantity = useSelector(getProductQuantity);
    const linkForTestRide = useSelector(getLinkForTestRide);
    const isExternalCartEnabled = useSelector(getIsExternalCartEnabled);
    const isItemAddedToCart = useSelector(getIsItemAddedToCart);
    const buttonAddToCartRef = useRef(null);
    const labelAddToCartRef = useRef(null);
    const labelTestRideRef = useRef(null);
    const dispatch = useDispatch();
    const { isInfiniteDesktopView, isMobileView } = useWindowSize();
    const currencyCode = priceAttribute?.currencyCode || '';
    const isStockValid = productStock > 0;
    const isPriceValid = Boolean(priceAttribute && priceAttribute?.amount > 0);
    const currentProductName = productDetailsData?.name ?? '';
    const selectedProductName = selectedProduct?.product?.name ?? '';
    const isTestRideAllow = Boolean(linkForTestRide && !isAccessoryOrApparel);
    const shouldShowWishlist = isAccessoryOrApparel && isWishlistEnabled;
    const quantitySelectorItems = useMemo(() => getDropdownItems(Math.min(productStock, MAX_STOCK_VALUE)), [productStock]);
    const quantitySelectorActiveItem = quantitySelectorItems.length < productQuantity ? 1 : productQuantity;
    const isAddToCardVisible = [eCommerceEnabledVariant, isPriceValid, ecommerceEnabled].every((item) => Boolean(item));
    const isCartDisabled = [!isVarianAvailable, !isStockValid, isNotEnoughStockLeft].some((item) => Boolean(item));
    const buttonAddToCartLabel = isCartDisabled ? outOfStockButtonLabel : addToCartButtonLabel;
    const cartIcon = isCartDisabled ? CartDisabled : Cart;
    const { gapValue, count } = getButtonPanelParam({
        filterOptionByProductType,
        isAddToCardVisible,
        firstButtonLabelSize: labelAddToCartRef?.current?.clientWidth,
        secondButtonLabelSize: labelTestRideRef?.current?.clientWidth,
        buttonAddToCartLabel,
        requestTestRideButtonLabel,
        blockWrapperWidth,
        isTestRideAllow,
    });
    const { partsAccessoriesMerch_addToCartPopUpMissingInfo_header: missingInfoHeader, partsAccessoriesMerch_addToCartPopUpYouSure_header: youSureHeader, partsAccessoriesMerch_addToCartPopUpCompatibility_button: compatibilityButton, partsAccessoriesMerch_addToCartPopUpAnyway_button: anywayButton, partsAccessoriesMerch_addToCartPopUpProceedToCart_button: proceedToCartButton, partsAccessoriesMerch_addToCartPopUpBackToPage_button: backToPageButton, partsAccessoriesMerch_addToCartPopUpMissingRequiredItems_description: missingRequiredItemsDescription, partsAccessoriesMerch_addToCartPopUpIncompatibleModel_description: incompatibleModelDescription, partsAccessoriesMerch_addToCartPopUpNeedToCheckCompatibility_description: needToCheckDescription, partsAccessoriesMerch_QuantitySelect_header: quantitySelectorHeader, } = labels;
    const onClickAddToCart = useCallback(async () => {
        if (isCartConvertedToOrder) {
            dispatch(setIsModalShowed({
                type: MODALS.ANOTHER_SESSION_MODAL,
                data: {
                    isAddToCart: true,
                },
            }));
            return;
        }
        if (isMaxStockValue) {
            setIsNotEnoughStockLeft(true);
            dispatch(addProductToCartFailure({
                error: CartErrorMessage.NOT_ENOUGH_STOCK_LEFT,
                inCart: MAX_STOCK_VALUE,
            }));
        }
        else {
            if (isExternalCartEnabled) {
                await dispatch(addProductToExternalCart([variantSku, ...requiredProductSku], productQuantity));
            }
            else {
                await dispatch(addProductToCart([variantSku, ...requiredProductSku], productQuantity, currencyCode, createCart, updateCart, preparedProductsUrls));
            }
        }
    }, [
        createCart,
        currencyCode,
        dispatch,
        isCartConvertedToOrder,
        isExternalCartEnabled,
        isMaxStockValue,
        preparedProductsUrls,
        productQuantity,
        requiredProductSku,
        updateCart,
        variantSku,
    ]);
    const proceedToCartModalData = useMemo(() => ({
        [NEED_CHECK]: {
            title: missingInfoHeader,
            description: getModalDescription(needToCheckDescription, selectedProductName, currentProductName),
            primaryButtonContent: anywayButton,
            secondaryButtonContent: compatibilityButton,
            primaryButtonHandler: onClickAddToCart,
            secondaryButtonHandler: selectModelHandler,
        },
        [INCOMPATIBLE]: {
            title: youSureHeader,
            description: getModalDescription(incompatibleModelDescription, selectedProductName, currentProductName),
            primaryButtonContent: anywayButton,
            secondaryButtonContent: backToPageButton,
            primaryButtonHandler: onClickAddToCart,
        },
        [NEED_REQUIRED_PARTS]: {
            title: youSureHeader,
            description: getModalDescription(missingRequiredItemsDescription, selectedProductName, currentProductName),
            primaryButtonContent: proceedToCartButton,
            secondaryButtonContent: backToPageButton,
            primaryButtonHandler: onClickAddToCart,
        },
    }), [
        anywayButton,
        backToPageButton,
        compatibilityButton,
        currentProductName,
        incompatibleModelDescription,
        missingInfoHeader,
        missingRequiredItemsDescription,
        needToCheckDescription,
        onClickAddToCart,
        proceedToCartButton,
        selectModelHandler,
        selectedProductName,
        youSureHeader,
    ]);
    const addAccessoryToCartHandler = useCallback(async (type = accessoriesCartState) => {
        if (type) {
            dispatch(setIsModalShowed({
                type: MODALS.PROCEED_TO_CART_MODAL,
                data: proceedToCartModalData[type],
            }));
            return;
        }
        await onClickAddToCart();
    }, [accessoriesCartState, dispatch, onClickAddToCart, proceedToCartModalData]);
    const changeQuantityHandler = (value) => {
        dispatch(setProductQuantity(Number(value)));
    };
    const onBookTestRideClick = (sku) => {
        return () => {
            setDigitalData(DigitalDataMutation.TEST_RIDE_SELECT_PDP, {
                sku,
            });
        };
    };
    const iAddToCartButtonOverflowed = buttonAddToCartRef.current &&
        labelAddToCartRef.current &&
        blockWrapperWidth &&
        isAccessoryOrApparel
        ? blockWrapperWidth - BUTTONS_PANEL_QUANTITY_WIDTH < labelAddToCartRef.current.clientWidth
        : false;
    useEffect(() => {
        return () => {
            dispatch(setProductQuantity(1));
        };
    }, [dispatch]);
    useEffect(() => {
        if (isItemAddedToCart) {
            (async () => {
                dispatch(setItemAddedToCart(false));
                await addAccessoryToCartHandler().finally(() => {
                    dispatch(setFasletAnalyticTrigger(false));
                });
            })();
        }
    }, [addAccessoryToCartHandler, dispatch, isItemAddedToCart]);
    useEffect(() => {
        if (!isMaxStockValue) {
            setIsNotEnoughStockLeft(false);
        }
    }, [isMaxStockValue]);
    useEffect(() => {
        setIsButtonsColumn(iAddToCartButtonOverflowed);
    }, [iAddToCartButtonOverflowed]);
    return (React.createElement(ButtonsGroupWrapperStyled, null,
        React.createElement(ButtonsGroupInnerStyled, { getter: gapValue, count: count, flexDirection: "row", isMobileView: isMobileView, flexWrap: isMobileView ? 'wrap' : undefined },
            isAddToCardVisible && (React.createElement(ButtonSelectorContainerStyled, { justifyContent: "space-between", alignItems: "center", gap: isInfiniteDesktopView ? '24px' : '16px', flexWrap: isButtonsColumn ? 'wrap' : undefined, isFullWidth: true },
                isAccessoryOrApparel && (React.createElement(QuantitySelectorWrapperStyled, { isRightToLeft: isRightToLeft },
                    React.createElement(Dropdown, { id: variantSku, isDisabled: !quantitySelectorItems.length, items: quantitySelectorItems, activeItem: String(quantitySelectorActiveItem), isRightToLeft: isRightToLeft, onChange: changeQuantityHandler, title: quantitySelectorHeader, useForMobile: true }))),
                React.createElement(ButtonStyled, { labelRef: labelAddToCartRef, buttonRef: buttonAddToCartRef, buttonVariant: CONTAINED_VARIANT, hasIconButton: TRAILING_ICON, size: SIZE_48, onClick: isAccessoryOrApparel ? () => addAccessoryToCartHandler() : onClickAddToCart, label: buttonAddToCartLabel, title: buttonAddToCartLabel, iconComponent: cartIcon, disabled: isCartDisabled, isRightToLeft: isRightToLeft }))),
            shouldShowWishlist && (React.createElement(Button, { className: wishlistClass, label: wishlistTooltipTitle, buttonVariant: isMobileView ? TEXT_VARIANT : GHOST_VARIANT, title: wishlistTooltipTitle, hasIconButton: isMobileView ? TRAILING_ICON : ICON_ONLY, iconComponent: Favorite, size: SIZE_48, onClick: onLike(variantSku, window.location.href.replace(window.location.origin, '')) })),
            isTestRideAllow && (React.createElement(ButtonLinkStyled, { to: linkForTestRide },
                React.createElement(Button, { labelRef: labelTestRideRef, buttonVariant: GHOST_VARIANT, hasIconButton: NO_ICON, size: SIZE_48, onClick: onBookTestRideClick(variantSku), label: requestTestRideButtonLabel, title: requestTestRideButtonLabel, isRightToLeft: isRightToLeft }))))));
}
ButtonsPanel.displayName = 'ButtonsPanel_Component';
